<template>
  <div class="dwp-space-y-8" v-loading="loading">
    <div class="dwp-flex dwp-justify-between dwp-items-center">
      <ArrowLeftIcon
        @click="$emit('update:value', false)"
        class="dwp-text-gray-500 dwp-w-6 dwp-h-6 dwp-cursor-pointer"
      />
      <div
        class="sm:dwp-text-2xl dwp-text-xl dwp-leading-none dwp-text-gray-700 dwp-font-medium"
      >Saved Lists</div>
      <div class="dwp-w-6 dwp-h-6"></div>
    </div>
    <div class="dwp-grid dwp-gap-4 dwp-grid-cols-2 sm:dwp-grid-cols-3">
      <SelectBox
        v-for="item in items"
        data-cy="library-item"
        :key="item.id"
        button-text="Import"
        @click.native="load(item)"
      >
        <div class="dwp-flex dwp-items-center dwp-justify-between dwp-w-full">
          <div
            v-if="item.tasks"
            class="dwp-inline-flex dwp-items-center dwp-px-2 dwp-py-1 dwp-rounded-full dwp-text-xs dwp-font-medium dwp-leading-4 dwp-bg-gray-200 dwp-text-gray-800 group-hover:dwp-text-primary-700 group-hover:dwp-bg-primary-100 dwp-transition-colors dwp-duration-200"
          >{{ item.tasks.length }} Tasks</div>
          <TrashIcon
            @click.prevent.stop="trash(item)"
            class="dwp-w-4 dwp-text-gray-400 hover:dwp-text-gray-700 dwp-opacity-0 group-hover:dwp-opacity-100 dwp-transition-all dwp-duration-200"
          />
        </div>
        <div>
          <h3
            class="dwp-leading-tight dwp-font-bold group-hover:dwp-text-primary-700 dwp-text-gray-700 dwp-transition-colors dwp-duration-200"
          >{{ item.title }}</h3>
          <div
            class="dwp-text-xs dwp-text-gray-500"
          >{{ item.created_at | date('MMMM do, yyyy, h:mm aa') }}</div>
        </div>
        <div class="dwp-text-sm dwp-text-gray-500" v-html="item.description"></div>
      </SelectBox>
      <div
        v-if="!Object.keys(items).length && !loading"
        class="dwp-w-full dwp-group dwp-select-none dwp-bg-gray-100 dwp-rounded-md"
      >
        <div
          class="dwp-p-4 sm:dwp-p-6 dwp-space-y-4 dwp-text-center dwp-h-full dwp-flex dwp-flex-col dwp-items-center dwp-justify-center"
        >
          <ZapIcon class="dwp-text-gray-400 dwp-pb-2 dwp-h-8" />
          <h3 class="dwp-leading-none dwp-font-bold dwp-text-gray-700">Save your lists!</h3>
          <p
            class="dwp-text-sm dwp-text-gray-500"
          >Create a new list and save it to your cloud library.</p>
          <VButton
            v-if="!Object.keys(lists).length"
            @click.native="newList = true; open = false;"
            type="outline"
          >Create New List</VButton>
          <Dropdown v-else :open.sync="dropdown" widthClass="dwp-w-64">
            <VButton type="outline" @click.native="dropdown = !dropdown">
              Save A List
              <ChevronDownIcon class="dwp-w-4 dwp-ml-2 dwp-leading-none" />
            </VButton>
            <template #list>
              <div style="max-height: 200px" class="dwp-overflow-y-auto">
                <div
                  @click="saveListModal.list = list; saveListModal.open = true; open = false;"
                  v-for="list in lists"
                  :key="list.id"
                  class="dwp-flex dwp-group dwp-items-center dwp-truncate dwp-cursor-pointer dwp-select-none dwp-px-4 dwp-py-3 dwp-text-sm dwp-leading-5 dwp-text-gray-700 hover:dwp-bg-gray-100 hover:dwp-text-gray-900 focus:dwp-outline-none focus:dwp-bg-gray-100 focus:dwp-text-gray-900"
                >{{ list.title }}</div>
              </div>
            </template>
          </Dropdown>
        </div>
      </div>
    </div>
    <div v-if="hasMore" class="dwp-flex dwp-items-center dwp-justify-center">
      <VButton type="outline" @click.native="fetch">Load More</VButton>
    </div>
  </div>
</template>

<script>
import SelectBox from "@/components/templates/SelectBoxTemplate.vue";
import VButton from "@/components/ui/VButton.vue";
import Dropdown from "@/components/ui/Dropdown.vue";
import { get, sync } from "vuex-pathify";
import {
  ZapIcon,
  ChevronDownIcon,
  TrashIcon,
  ArrowLeftIcon,
} from "vue-feather-icons";
import currentList from "@/mixins/current-list";

export default {
  mixins: [currentList],
  props: {
    value: String | Boolean,
  },
  data() {
    return {
      loading: false,
      hasMore: false,
      dropdown: false,
    };
  },
  components: {
    SelectBox,
    ZapIcon,
    TrashIcon,
    ChevronDownIcon,
    ArrowLeftIcon,
    Dropdown,
    VButton,
  },
  computed: {
    newList: sync("ui/modal@list.new.open"),
    team: get("user/user@team"),
    items: get("team-lists-library/items"),
    saveListModal: sync("ui/modal@list.save"),
    open: sync("ui/modal@list.import.open"),
    menu: sync("ui/menu@open"),
  },
  async mounted() {
    this.$store.dispatch("user/logEvent", {
      name: "team_library_opened",
    });

    this.loading = true;
    await this.fetch();
    this.loading = false;
  },
  methods: {
    async fetch() {
      this.loading = true;
      let result;
      try {
        result = await this.$store.dispatch(
          "team-lists-library/openDBChannel",
          {
            clauses: { limit: 20, where: [["status", "==", "published"]] },
          }
        );
      } catch (e) {
        alert("Something went wrong, please try again.");
      }

      this.hasMore = typeof result.done !== "undefined" && !result.done;
      this.loading = false;
    },

    async load(list) {
      this.loading = true;
      let id;

      try {
        id = await this.$store.dispatch("lists/cloneToTeam", {
          list,
          moduleName: "team-tasks-library",
        });
      } catch (e) {
        console.error(e);
        alert("Something went wrong, please try again.");
      }

      this.$nextTick(() => {
        this.currentListId = id;
      });
      this.open = false;
      this.menu = false;
      this.loading = false;

      this.$store.dispatch("user/logEvent", {
        name: "team_library_downloaded",
        id: list.id,
      });
    },

    async trash(item) {
      let r = confirm("Are you sure?");
      if (r) {
        this.loading = true;
        await this.$store.dispatch("team-lists-library/patch", {
          id: item.id,
          status: "trashed",
        });
        this.loading = false;
      }
    },
  },
};
</script>